// create a api using createApi from @reduxjs/toolkit/query/react for the Group Model
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { GradeWithStudents } from 'src/config/api/models/Grade';
import { Group, NewGroupListItem, getGroupsMapper } from 'src/config/api/models/Group';
import apiRoutesV2, { baseURL } from 'src/config/api/routes';
import { RootState } from 'src/modules/shared/store';

type GroupsByUserParams = {
  userId: string;
  bookVersionId?: string;
};

//TODO - Cleanup those types
type StudentParams = {
  studentId: string;
  gradeId: string;
};

export type AddCustomGroupParams = {
  bookId: string;
  groupData: { name: string; students: StudentParams[] };
};

export type UpdateCustomGroupParams = {
  groupId: string;
  groupData: { name: string; students: StudentParams[] };
};
export type BookGroup = {
  id: string;
  label: string;
  amountOfStudents: number;
  isDefault: boolean;
}

export const groupsApi = createApi({
  reducerPath: 'groupsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getGroupsByUser: builder.query<Group[], GroupsByUserParams>({
      query: ({ userId, bookVersionId }) => ({
        url: apiRoutesV2.groupsByUserRelativePath(userId, bookVersionId),
        method: 'GET',
      }),
      transformResponse: (response) => getGroupsMapper(response),
    }),
    getGroupsManagement: builder.query<NewGroupListItem[], void>({
      query: () => ({
        url: apiRoutesV2.getGroupsManagement(),
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      keepUnusedDataFor: 0,
    }),
    getGradesByBook: builder.query<GradeWithStudents[], { bookId: string; groupId?: string }>({
      query: ({ bookId, groupId }) => ({
        url: apiRoutesV2.getGradesByBook(bookId, groupId),
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
    }),
    updateGroup: builder.mutation<void, { groupId: string; bookId: string }>({
      query: ({ groupId, bookId }) => ({
        url: apiRoutesV2.toggleGradesVisibility(groupId, bookId),
        method: 'PATCH',
      }),
      transformResponse: (response: any) => response.data,
    }),
    updateCustomGroup: builder.mutation<void, UpdateCustomGroupParams>({
      query: ({ groupId, groupData }) => ({
        url: apiRoutesV2.updateCustomGroup(groupId),
        method: 'PUT',
        body: groupData,
      }),
    }),
    addCustomGroup: builder.mutation<void, AddCustomGroupParams>({
      query: ({ bookId, groupData }) => ({
        url: apiRoutesV2.addCustomGroup(bookId),
        method: 'POST',
        body: groupData,
      }),
    }),
    deleteCustomGroup: builder.mutation<void, { groupId: string }>({
      query: ({ groupId }) => ({
        url: apiRoutesV2.deleteCustomGroup(groupId),
        method: 'DELETE',
      }),
    }),
    getGroupsByBook: builder.query<BookGroup[], string>({
      query: (bookId) => ({
        url: apiRoutesV2.bookGroups(bookId),
        method: 'GET',
      }),
      transformResponse: (response: { data: BookGroup[] }) => { return response.data  }
    }),
  }),
});

export const {
  useGetGroupsByUserQuery,
  useGetGroupsManagementQuery,
  useGetGradesByBookQuery,
  useUpdateGroupMutation,
  useAddCustomGroupMutation,
  useDeleteCustomGroupMutation,
  useUpdateCustomGroupMutation,
  useGetGroupsByBookQuery,
} = groupsApi;
